import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { FareScrollStripCircleArrow } from '@/assets/icons/FareScrollStripCircleArrow'
import styles from './FareHorizontalScrollStrip.module.scss'
import { Container } from '@cleartrip/ct-design-components'

const CARD_STRIDE_PX = 232
const MAX_CARDS_PER_SCROLL = 4
const SCROLL_EPS = 2
const FORWARD='forward'
const BACKWARD='backward'

export type FareHorizontalScrollStripProps = {
    children: React.ReactNode
    totalFareCount?: number
    scrollTrackClassName?: string
    scrollResetKey?: string | number
    topInsetPx?: number
}

type UiMode = typeof FORWARD | typeof BACKWARD

const FareHorizontalScrollStrip: React.FC<FareHorizontalScrollStripProps> = ({
    children,
    scrollTrackClassName,
    scrollResetKey,
    topInsetPx
}) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)
    const [uiMode, setUiMode] = useState<UiMode>(FORWARD)

    const syncScrollState = useCallback(() => {
        const el = scrollRef.current
        if (!el) return
        const scrollTrackChildren = el.children
        if (scrollTrackChildren.length === 0) {
            setCanScrollLeft(false)
            setCanScrollRight(false)
            return
        }
        const trackRect = el.getBoundingClientRect()
        const firstRect = (scrollTrackChildren[0] as HTMLElement).getBoundingClientRect()
        const lastRect = (scrollTrackChildren[scrollTrackChildren.length - 1] as HTMLElement).getBoundingClientRect()
        setCanScrollLeft(firstRect.left < trackRect.left - SCROLL_EPS)
        setCanScrollRight(lastRect.right > trackRect.right + SCROLL_EPS)
    }, [])

    useLayoutEffect(() => {
        if (scrollResetKey === undefined) return
        const el = scrollRef.current
        if (el) el.scrollLeft = 0
        setUiMode(FORWARD)
        syncScrollState()
    }, [scrollResetKey, syncScrollState])

    useLayoutEffect(() => {
        const el = scrollRef.current
        syncScrollState()
        const rafId = requestAnimationFrame(() => syncScrollState())
      
        let resizeObserver: ResizeObserver | undefined
        if (el && typeof ResizeObserver !== 'undefined') {
          resizeObserver = new ResizeObserver(() => syncScrollState())
          resizeObserver.observe(el)
        }
      
        return () => {
          cancelAnimationFrame(rafId)
          resizeObserver?.disconnect()
        }
      }, [children, syncScrollState])

    useEffect(() => {
        const onlyForward = !canScrollLeft && canScrollRight
        const onlyBackward = canScrollLeft && !canScrollRight
        if (onlyForward) {
            setUiMode(FORWARD)
        } else if (onlyBackward) {
            setUiMode(BACKWARD)
        }
    }, [canScrollLeft, canScrollRight])

    const showBackwardChrome = uiMode === BACKWARD

    const scrollByStride = (direction: UiMode) => {
        const el = scrollRef.current
        if (!el) return
        const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth)
        const remaining =
            direction === FORWARD ? maxScroll - el.scrollLeft : el.scrollLeft
        const maxDelta = CARD_STRIDE_PX * MAX_CARDS_PER_SCROLL
        const delta = Math.min(maxDelta, remaining)
        if (delta <= SCROLL_EPS) return
        const left = direction === FORWARD ? delta : -delta
        el.scrollBy({ left, behavior: 'auto' })
    }

    const onArrowClick = () => {
        if (uiMode === FORWARD && canScrollRight) {
            scrollByStride(FORWARD)
        } else if (uiMode === BACKWARD && canScrollLeft) {
            scrollByStride(BACKWARD)
        }
    }

    const showArrow = canScrollLeft || canScrollRight

    return (
        <Container className="p-relative flex flex-column flex-1" css={{ minWidth: 0 }}>
            <Container
                ref={scrollRef}
                className={`flex flex-nowrap w-100p ox-scroll no-scrollbar ${styles.scrollTrack}${scrollTrackClassName ? ` ${scrollTrackClassName}` : ''}`}
                css={{
                    minWidth: 0,
                    ...(topInsetPx !== undefined ? { paddingTop: `${topInsetPx}px` } : {})
                }}
                onScroll={syncScrollState}
            >
                {children}
            </Container>
            {showArrow && (
                <button
                    type="button"
                    className={`p-absolute t-50 vertically-centre z-10 m-0 p-0 c-pointer bg-white
                        ${styles.arrowBtn} ${showBackwardChrome ? styles.atEnd : ''}`}
                    aria-label={showBackwardChrome ? 'Show previous fares' : 'Show more fares'}
                    onClick={onArrowClick}
                >
                    <FareScrollStripCircleArrow rotationDeg={showBackwardChrome ? 0 : 180} />
                </button>
            )}
        </Container>
    )
}

export default FareHorizontalScrollStrip
